import * as React from "react";
import { CurrentUser, Expense, PreviewAttachment, ResourceListFilterType } from "../../types";
import PageLoader from "../Page/PageLoader";
import { WithTranslation, withTranslation } from "react-i18next";
import ResourceTable from "../Resource/ResourceTable";
import ResourceTableRow from "../Resource/ResourceTableRow";
import ResourceTableRowData from "../Resource/ResourceTableRowData";
import moment from "../../utilities/Moment";
import { connect } from "react-redux";
import { AppState } from "../../store";
import NumberFormatter from "../../utilities/NumberFormatter";
import CardEmptyInfo from "../Card/CardEmptyInfo";
import { ExpensesController } from "../../controllers";
import ResourceTableRowDataLink from "../Resource/ResourceTableRowDataLink";
import { withRouter, RouteComponentProps } from 'react-router';
import { AnyAction, Dispatch } from "redux";
import LocalStorage, { LocalStorageKey } from "../../LocalStorage";
import { Style } from "../../styles";
import { showExpenseModal } from "../../store/modals/actions";
import ButtonPanel from "../Button/ButtonPanel";

interface IStateToProps {
    currentUser: CurrentUser
}

type IProps = {
    expenseIds: string[]
    onLinkClick: (expenseIds: string[]) => void
} & RouteComponentProps & WithTranslation & IStateToProps & IDispatchToProps

interface IDispatchToProps {
    showExpenseModal: typeof showExpenseModal
}

interface IState {
    expenses: Expense[]
    currentPage: number
    perPage: number
    totalPages: number
    filters: any
    didInitialLoad: boolean
    isFetching: boolean
    selectedExpenses: string[]
    sortValue: string
    searchValue: string
}

class LinkExpenses extends React.Component<IProps, IState> {
    constructor(props) {
        super(props)

        this.state = {
            expenses: [],
            currentPage: 0,
            perPage: LocalStorage.get(LocalStorageKey.PER_PAGE, 25),
            totalPages: 0,
            filters: {},
            didInitialLoad: false,
            searchValue: '',
            sortValue: LocalStorage.get(LocalStorageKey.EXPENSE_SORT_VALUE, 'invoiced_on_desc'),
            isFetching: false,
            selectedExpenses: []
        }

        this.fetchExpenses = this.fetchExpenses.bind(this)
        this.onNewExpensesClick = this.onNewExpensesClick.bind(this)
        this.onExpenseClick = this.onExpenseClick.bind(this)
        this.onExpenseFormSubmit = this.onExpenseFormSubmit.bind(this)
        this.onFiltersChange = this.onFiltersChange.bind(this)
        this.onSortValueChange = this.onSortValueChange.bind(this)
        this.onPerPageChange = this.onPerPageChange.bind(this)
        this.onSelectionChange = this.onSelectionChange.bind(this)
        this.onTableRowSelectionChange = this.onTableRowSelectionChange.bind(this)
        this.onSearchChange = this.onSearchChange.bind(this)
        this.onSearchSubmit = this.onSearchSubmit.bind(this)
        this.onClearFilter = this.onClearFilter.bind(this)
        this.onLinkClick = this.onLinkClick.bind(this)
    }

    componentDidMount(): void {
        this.fetchExpenses(1)
    }

    async fetchExpenses(page: number = 1) {
        const { searchValue, filters, sortValue, perPage } = this.state
        try {
            let parameters = {
                search: searchValue,
                order: sortValue,
                ...filters
            }

            this.setState({ isFetching: true })

            const { current_page, total_pages, expenses } = await ExpensesController.getExpenses({
                page: page,
                per_page: perPage,
                ...parameters
            })

            this.setState({
                ...this.state,
                expenses: expenses,
                currentPage: current_page,
                totalPages: total_pages,
                didInitialLoad: true,
                isFetching: false
            })
        } catch (ex) {
            console.error(ex)
        }
    }

    onNewExpensesClick() {
        const { showExpenseModal } = this.props

        showExpenseModal({
            expense: {},
            onSubmit: (expense) => {
                this.onLinkClick([expense.id])
            }
        })
    }

    onExpenseClick(expense: Expense) {
        this.props.showExpenseModal({
            expense: { id: expense.id },
            onSubmit: this.onExpenseFormSubmit
        })
    }

    onExpenseFormSubmit(expense: Expense) {
        const { expenses } = this.state

        const expenseIndex = expenses.findIndex(e => e.id === expense.id)

        if (expenseIndex === -1) {
            this.setState({
                expenses: [
                    expense,
                    ...expenses
                ]
            })
        } else {
            expenses[expenseIndex] = expense
            this.setState({ expenses: [...expenses] })
        }

    }

    onFiltersChange(filters: any) {
        this.setState({ ...this.state, filters: filters }, () => {
            this.fetchExpenses(1)
        })
    }

    onSortValueChange(sortValue: string) {
        LocalStorage.set(LocalStorageKey.EXPENSE_SORT_VALUE, sortValue)

        this.setState({ sortValue: sortValue }, () => {
            this.fetchExpenses(1)
        })
    }

    onPerPageChange(perPage: number) {
        LocalStorage.set(LocalStorageKey.PER_PAGE, perPage)
        this.setState({ currentPage: 1, perPage: perPage }, () => {
            this.fetchExpenses(1)
        })
    }

    onSelectionChange(selectedExpenses: string[]) {
        this.setState({ selectedExpenses: selectedExpenses })
    }

    onTableRowSelectionChange(selected: boolean, ledgerItemId: string) {
        const { selectedExpenses } = this.state

        if (selected) {
            this.setState({ selectedExpenses: [...selectedExpenses, ledgerItemId] })
        } else {
            this.setState({ selectedExpenses: selectedExpenses.filter(selectedLedgerItemId => selectedLedgerItemId !== ledgerItemId) })
        }
    }

    onSearchChange(searchValue) {
        this.setState({ ...this.state, searchValue: searchValue })
    }

    onSearchSubmit(searchValue) {
        this.setState({ ...this.state, searchValue: searchValue }, () => {
            this.fetchExpenses(1)
        })
    }

    onClearFilter() {
        this.setState({
            searchValue: '',
            filters: {}
        }, () => this.fetchExpenses(1))
    }

    onLinkClick(expenseIds: string[]) {
        const { onLinkClick } = this.props

        // Clear the selected ledger item ids
        this.setState({ selectedExpenses: [] })

        // Call the parent component's onLinkClick function
        onLinkClick(expenseIds)
    }

    render() {
        const { expenseIds, t, currentUser } = this.props
        const { didInitialLoad, expenses, searchValue, filters, currentPage, perPage, totalPages, isFetching, selectedExpenses, sortValue } = this.state
        const { workspace: { setting } } = currentUser

        const filtersActive = searchValue?.length > 0 || Object.keys(filters).length > 0

        return (
            <>
                {!didInitialLoad && <PageLoader />}
                {didInitialLoad && <ResourceTable
                    data={expenses.filter(expense => !expenseIds.includes(expense.id))}
                    headers={[
                        { title: t('LinkExpenses::Name') },
                        { title: t('LinkExpenses::Category') },
                        { title: t('LinkExpenses::Date'), align: 'right' },
                        { title: t('LinkExpenses::Amount'), align: 'right' },
                        { title: '', align: 'right' },
                    ]}
                    actionsLeft={[
                        <ButtonPanel
                            icon='plus'
                            text={t('LinkExpenses::New expense')}
                            onClick={this.onNewExpensesClick}
                        />
                    ]}
                    renderRow={(expense: Expense) => {
                        return (
                            <ResourceTableRow
                                key={expense.id}
                                selected={selectedExpenses.includes(expense.id)}
                                onSelectionChange={(selected) => this.onTableRowSelectionChange(selected, expense.id)}
                            >
                                <ResourceTableRowData maxWidth='150px' ellipse onClick={() => this.onExpenseClick(expense)}>
                                    {expense.name}
                                </ResourceTableRowData>
                                <ResourceTableRowData maxWidth='150px' ellipse onClick={() => this.onExpenseClick(expense)}>
                                    {expense.category?.name || '-'}
                                </ResourceTableRowData>
                                <ResourceTableRowData textAlign='right' onClick={() => this.onExpenseClick(expense)}>
                                    {moment(expense.invoiced_on).format(setting.date_format)}
                                </ResourceTableRowData>
                                <ResourceTableRowData
                                    textAlign='right'
                                    onClick={() => this.onExpenseClick(expense)} style={{
                                        fontWeight: 'bold',
                                        color: Style.color.brandDanger
                                    }}>
                                    {NumberFormatter.formatCurrency(expense.currency, setting.number_format, -(expense.amount))}
                                </ResourceTableRowData>
                                <ResourceTableRowData textAlign='right' maxWidth='150px' ellipse onClick={() => this.onLinkClick([expense.id])}>
                                    {!expenseIds.includes(expense.id) && <ResourceTableRowDataLink>
                                        {t('LinkExpenses::Link')}
                                    </ResourceTableRowDataLink>}
                                </ResourceTableRowData>
                            </ResourceTableRow>
                        )
                    }}
                    renderEmpty={<CardEmptyInfo
                        icon={filtersActive ? 'search' : 'expense'}
                        description={t('LinkExpenses::No expenses found')}
                        descriptionActionText={filtersActive ? t('LinkExpenses::Clear filters') : null}
                        onDescriptionActionClick={filtersActive ? this.onClearFilter : null}
                    />}
                    filters={[
                        { name: 'name', label: t('Expenses::Name'), type: ResourceListFilterType.STRING },
                        { name: 'invoiced_on', label: t('Expenses::Invoice date'), type: ResourceListFilterType.DATE },
                        { name: 'amount', label: t('Expenses::Amount'), type: ResourceListFilterType.NUMBER },
                        { name: 'currency', label: t('Expenses::Currency'), type: ResourceListFilterType.STRING },
                        { name: 'contact_id', label: t('Expenses::Contact'), type: ResourceListFilterType.SINGLE_RESOURCE, resourceType: 'contact', isValidNewOption: () => false },
                        { name: 'project_id', label: t('Expenses::Project'), type: ResourceListFilterType.SINGLE_RESOURCE, resourceType: 'project', isValidNewOption: () => false },
                        { name: 'supplier_id', label: t('Expenses::Supplier'), type: ResourceListFilterType.SINGLE_RESOURCE, resourceType: 'company', isValidNewOption: () => false },
                        { name: 'booked_on', label: t('Expenses::Booked on'), type: ResourceListFilterType.DATE },
                        { name: 'paid_on', label: t('Expenses::Paid on'), type: ResourceListFilterType.DATE },
                        { name: 'billable', label: t('Expenses::Billable'), type: ResourceListFilterType.SINGLE_OPTION, options: [{ label: t('Expenses::Yes'), value: String(true) }, { label: t('Expenses::No'), value: String(false) }] },
                        { name: 'billed', label: t('Expenses::Billed'), type: ResourceListFilterType.SINGLE_OPTION, options: [{ label: t('Expenses::Yes'), value: String(true) }, { label: t('Expenses::No'), value: String(false) }] },
                        { name: 'created_at', label: t('Expenses::Created date'), type: ResourceListFilterType.DATE },
                    ]}
                    onFiltersChange={this.onFiltersChange}
                    sortOptions={[
                        { label: '-', value: '-' },
                        { label: t('LinkedExpenses::Name (A-Z)'), value: 'name_asc' },
                        { label: t('LinkedExpenses::Name (Z-A)'), value: 'name_desc' },
                        { label: t('LinkedExpenses::Date ↑'), value: 'invoiced_on_asc' },
                        { label: t('LinkedExpenses::Date ↓'), value: 'invoiced_on_desc' },
                        { label: t('LinkedExpenses::Amount (excl. VAT) ↑'), value: 'net_total_asc' },
                        { label: t('LinkedExpenses::Amount (excl. VAT) ↓'), value: 'net_total_desc' },
                        { label: t('LinkedExpenses::Amount ↑'), value: 'amount_asc' },
                        { label: t('LinkedExpenses::Amount ↓'), value: 'amount_desc' },
                    ]}
                    sortValue={sortValue}
                    onSortChange={this.onSortValueChange}
                    promotedBulkActions={[
                        { icon: 'link', content: t('LinkExpenses::Link to transaction'), onAction: () => this.onLinkClick(selectedExpenses) }
                    ]}
                    pagination={{
                        page: currentPage,
                        perPage: perPage,
                        pageCount: totalPages
                    }}
                    onPageChange={(page) => this.fetchExpenses(page)}
                    onPerPageChange={this.onPerPageChange}
                    isLoading={isFetching}
                    stickyHeader={true}
                    selectedItems={selectedExpenses}
                    onSelectionChange={this.onSelectionChange}
                    searchValue={searchValue}
                    onSearchChange={this.onSearchChange}
                    onSearchSubmit={this.onSearchSubmit}
                    maxHeight='25vh'
                />}
            </>
        )
    }
}

const mapStateToProps = (state: AppState): IStateToProps => {
    const {
        authentication: {
            currentUser,
        }
    } = state

    return {
        currentUser: currentUser,
    }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
    return {
        showExpenseModal: (options) => dispatch(showExpenseModal(options)),
    }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(LinkExpenses)) as any) as any
